import { AlertCircle } from 'lucide-react'
import { LuInstagram as Instagram } from 'react-icons/lu'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

interface Props {
  /** The athlete tapped cancel / don't allow on Instagram, as opposed to something breaking */
  cancelled: boolean
  /** A specific reason from our server, already written for the athlete */
  message?: string | null
  onRetry: () => void
  onLeave: () => void
}

/** Shown when Instagram did not end up connected. Written for the athlete, not for developers. */
const InstagramConnectFailed = ({ cancelled, message, onRetry, onLeave }: Props) => (
  <div className="w-full max-w-2xl mx-auto pb-4 min-h-screen flex items-center justify-center">
    <Card variant="dark" className="overflow-hidden w-full">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-4">
          <div className="bg-linear-to-r from-[#FF3A89] to-[#FFC702] p-4 rounded-full">
            <Instagram className="h-8 w-8 text-[#FFFFFF]" />
          </div>
        </div>
        <CardTitle className="text-foreground text-xl">
          {cancelled ? "Instagram isn't connected" : "We couldn't connect Instagram"}
        </CardTitle>
        <CardDescription className="text-gray-600">
          {cancelled
            ? 'You chose not to give Athios access, so nothing was connected.'
            : 'Something went wrong on the way back from Instagram. Nothing on your account has changed.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="text-center space-y-6">
        {message && !cancelled && (
          <Alert className="bg-red-500/10 border-red-500/20 text-left">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-700">{message}</AlertDescription>
          </Alert>
        )}

        {cancelled ? (
          <p className="text-sm text-gray-700 max-w-md mx-auto">
            Connecting lets brands see your real follower numbers and audience, which helps you get
            picked for deals. We only read your stats. We never post or message for you. You can
            connect any time from Settings.
          </p>
        ) : (
          <div className="space-y-2 text-left max-w-md mx-auto">
            <p className="text-sm text-gray-700">A couple of things to check, then try again:</p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Your Instagram needs to be a Professional account (Creator or Business)</li>
              <li>• Tap Allow on everything Instagram asks for</li>
            </ul>
          </div>
        )}

        <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-center">
          <Button
            variant="outline"
            onClick={onLeave}
            className="border-foreground/20 text-gray-700 hover:bg-foreground/5"
          >
            Not now
          </Button>
          <Button onClick={onRetry} variant="lime">
            {cancelled ? 'Connect Instagram' : 'Try again'}
          </Button>
        </div>
      </CardContent>
    </Card>
  </div>
)

export default InstagramConnectFailed
